import { COPY_POLICIES } from "./copy-policy";
import { validateScalarSlotLength } from "./scalar-slot-validation";
export const CAROUSEL_SCENE_MAX_LENGTHS = {
    title: COPY_POLICIES.title.maxLength,
    description: COPY_POLICIES.description.maxLength,
};
/**
 * The baked artwork positions the stylesheet knows how to place.
 *
 * A CLOSED set, and closed for a hard reason: an id becomes the class
 * `carousel-layer--{id}` and indexes the component's paint-order table, so an id
 * outside this list lands unpositioned with no stacking at all — no error, just
 * a broken scene. @diality/ui asserts at compile time that its own union and
 * this list agree.
 */
export const CAROUSEL_LAYER_IDS = [
    "large-composite",
    "right-composite",
    "intro",
    "engineered-machine",
    "engineered-person-front",
    "engineered-person-profile",
    "space",
    "treatment-chair",
    "treatment-machine",
];
export const CAROUSEL_SCREENS = [
    "desktop",
    "mobile",
];
/**
 * The width at and below which a carousel recomposes its subjects — and,
 * therefore, at and below which a visitor is sent the mobile picture.
 *
 * DECLARED ONCE, because three things have to agree about it: the stylesheet's
 * portrait recomposition, the `media` attribute of each subject's desktop
 * source, and the component's own preload, which must fetch the file the browser
 * is actually going to draw. Two of the three agreeing is a carousel that serves
 * desktop artwork into the mobile arrangement.
 *
 * Deliberately NOT the 640px step the shared per-screen mechanism uses. That
 * mechanism renders both pictures and hides one, which downloads both; this one
 * fetches only the matching file, and it switches where this component's own
 * geometry changes rather than where another component's does.
 */
export const CAROUSEL_MOBILE_MAX_WIDTH = 900;
/** The media query under which a subject's DESKTOP picture is the one served. */
export const CAROUSEL_DESKTOP_MEDIA = `(min-width: ${CAROUSEL_MOBILE_MAX_WIDTH + 1}px)`;
/**
 * What an Editor calls each baked position.
 *
 * Composition-descriptive, never named for the artwork that fills them today: a
 * kind is a treatment a future carousel can reuse, and "Back subject" survives a
 * change of subject in a way "Machine" does not.
 */
const LAYER_LABELS = {
    "large-composite": "Large image",
    "right-composite": "Image right",
    intro: "Subject",
    "engineered-machine": "Back subject",
    "engineered-person-front": "Front subject",
    "engineered-person-profile": "Profile subject",
    space: "Subject",
    "treatment-chair": "Near subject",
    "treatment-machine": "Far subject",
};
const SCREEN_LABELS = {
    desktop: "desktop",
    mobile: "mobile",
};
/** A subject's two pictures are two slots, named for the position AND the screen. */
export function carouselSlotKey(layerId, screen) {
    return `${layerId}-${screen}`;
}
/** Legacy subject slots and prepared compositions use the original image
 * without cropping or trimming transparency. */
export const CAROUSEL_SUBJECT_SLOTS = CAROUSEL_LAYER_IDS.flatMap((layerId) => CAROUSEL_SCREENS.map((screen) => ({
    key: carouselSlotKey(layerId, screen),
    label: `${LAYER_LABELS[layerId]} (${SCREEN_LABELS[screen]})`,
    unframed: true,
    targets: [],
})));
/** Only the two composite presets are offered for creation. Legacy kinds stay
 * in the declaration so existing references can still be edited and rendered. */
export const CAROUSEL_SCENE_KINDS = [
    {
        id: "large-image",
        label: "Large image",
        description: "One oversized composition, based on the opening slide. Upload one desktop image and one mobile image.",
        layers: ["large-composite"],
    },
    {
        id: "image-right",
        label: "Image right",
        description: "One combined composition beside the card; above it on mobile. Upload one desktop image and one mobile image.",
        layers: ["right-composite"],
    },
    {
        id: "full-width",
        legacy: true,
        label: "Full-width subject",
        description: "One subject, centred, overhanging the top of the stage.",
        layers: ["intro"],
    },
    {
        id: "staggered-trio",
        legacy: true,
        label: "Three subjects, staggered",
        description: "A subject back-left, a second front-centre, and a third in profile to the right.",
        layers: [
            "engineered-machine",
            "engineered-person-front",
            "engineered-person-profile",
        ],
    },
    {
        id: "off-centre",
        legacy: true,
        label: "Single subject, off-centre",
        description: "One subject right of centre, at full height.",
        layers: ["space"],
    },
    {
        id: "wide-pair",
        legacy: true,
        label: "Two subjects, wide apart",
        description: "A near subject at the left, in front of a far one at the right.",
        layers: ["treatment-chair", "treatment-machine"],
    },
];
/** The scalar a scene records its kind in. Never a declared scalar slot. */
export const CAROUSEL_SCENE_KIND_KEY = "sceneKind";
/** The repeat group the scenes live in. */
export const CAROUSEL_SCENES_KEY = "scenes";
/** A carousel needs somewhere to travel to. One scene leads back to itself. */
export const CAROUSEL_MINIMUM_SCENES = 2;
export function carouselSceneKind(id) {
    if (!id)
        return undefined;
    return CAROUSEL_SCENE_KINDS.find((kind) => kind.id === id);
}
function isRecord(value) {
    return typeof value === "object" && value !== null && !Array.isArray(value);
}
/** Does this part say anything at all? */
function saysNothing(value) {
    return typeof value !== "string" || value.trim() === "";
}
/** Does this reference actually name a picture? */
function namesMedia(value) {
    if (!isRecord(value))
        return false;
    const { assetId, cropId } = value;
    return (typeof assetId === "string" &&
        assetId.trim() !== "" &&
        typeof cropId === "string" &&
        cropId.trim() !== "");
}
/**
 * The rules, in the Editor's reading order — one rule, one message.
 *
 * Absent and unrecognised are ONE message for a kind, because they are one
 * defect from where the Editor stands and because splitting them would put
 * `Every scene needs a kind` and a superstring of it in the same table.
 *
 * EVERY part of a scene is required. That is this component's position rather
 * than `compose-person-grid`'s, and the reason is that a scene has no empty
 * state to draw: a card with no title is blank, and a missing subject is a hole
 * in a composition built around it. The picture rule goes further and is worth
 * stating — the component refuses to advance to a scene whose artwork has not
 * decoded, so ONE unfilled subject can wedge the carousel for a visitor.
 *
 * Deliberate non-rules: how many scenes beyond the floor, their order, duplicate
 * titles, the same picture used in more than one scene, and the size or shape of
 * its artwork. Scene copy length is declared by the scalar schema and enforced
 * here through the shared scalar-slot validator.
 */
export function validateCarousel(value) {
    if (!isRecord(value)) {
        return { valid: false, reason: "The Carousel block is malformed" };
    }
    if (saysNothing(value.name)) {
        return { valid: false, reason: "A carousel needs a name" };
    }
    const scenes = value.scenes;
    if (!Array.isArray(scenes)) {
        return { valid: false, reason: "The Carousel block is malformed" };
    }
    if (scenes.length < CAROUSEL_MINIMUM_SCENES) {
        return { valid: false, reason: "A carousel needs at least two scenes" };
    }
    const checked = [];
    for (const scene of scenes) {
        if (!isRecord(scene)) {
            return { valid: false, reason: "The Carousel block is malformed" };
        }
        const kindId = typeof scene.sceneKind === "string" ? scene.sceneKind : "";
        const kind = carouselSceneKind(kindId);
        if (!kind) {
            return {
                valid: false,
                reason: "Every scene needs a kind this carousel knows",
            };
        }
        if (saysNothing(scene.title)) {
            return { valid: false, reason: "Every scene needs a title" };
        }
        const titleLength = validateScalarSlotLength({ label: "Title", maxLength: CAROUSEL_SCENE_MAX_LENGTHS.title }, scene.title);
        if (!titleLength.valid)
            return titleLength;
        if (saysNothing(scene.description)) {
            return { valid: false, reason: "Every scene needs a description" };
        }
        const descriptionLength = validateScalarSlotLength({
            label: "Description",
            maxLength: CAROUSEL_SCENE_MAX_LENGTHS.description,
        }, scene.description);
        if (!descriptionLength.valid)
            return descriptionLength;
        const held = isRecord(scene.media) ? scene.media : {};
        const placed = new Set();
        for (const layerId of kind.layers) {
            for (const screen of CAROUSEL_SCREENS) {
                placed.add(carouselSlotKey(layerId, screen));
            }
        }
        for (const key of placed) {
            if (!namesMedia(held[key])) {
                return {
                    valid: false,
                    reason: "Every subject needs a desktop picture and a mobile one",
                };
            }
        }
        // A picture in a position this kind has nowhere to draw would be kept and
        // never shown — the silent drop `filled-slot-never-silently-dropped` exists
        // to prevent, applied per scene rather than per block.
        for (const [key, ref] of Object.entries(held)) {
            if (!placed.has(key) && namesMedia(ref)) {
                return {
                    valid: false,
                    reason: "A scene holds a picture its arrangement does not show",
                };
            }
        }
        // Rebuilt field by field, so an unknown key cannot ride into storage and
        // so the stored media map holds exactly the positions this kind places.
        const media = {};
        for (const key of placed) {
            const ref = held[key];
            media[key] = { assetId: ref.assetId, cropId: ref.cropId };
        }
        checked.push({
            sceneKind: kind.id,
            title: scene.title,
            description: scene.description,
            media,
        });
    }
    return {
        valid: true,
        carousel: { name: value.name, scenes: checked },
    };
}
//# sourceMappingURL=carousel.js.map